import {Button, Grid, Heading} from '@primer/react-brand/esm'
import {clsx} from 'clsx'
import {Fragment, useEffect, useMemo, useRef} from 'react'
import {usePrefersReducedMotion} from '@github-ui/use-prefers-reduced-motion'
import {OSAwareDownloadActionMenu} from '@github-ui/swp-core/components/OSAwareDownloadActionMenu'
import {useIntersectionObserver} from '@github-ui/swp-core/lib/hooks/use-intersection-observer'

import {hpcAttrs} from '@github-ui/hpc'
import {getAnalyticsEvent} from '@github-ui/swp-core/lib/utils/analytics'

import type {Cta, DownloadMenu, SignUpField} from '../../../Index.types'
import {MonaHead, type MonaHeadProps} from '../MonaHead/MonaHead'
import {EyebrowBanner, type EyebrowBannerProps} from '../EyebrowBanner/EyebrowBanner'
import {SignUpForm} from '../SignUpForm/SignUpForm'

import {parseIntroTitle} from './utils/intro-title'
import {useMotion} from './Intro.motion'

import Styles from './Intro.module.css'

export interface IntroProps {
  eyebrowBanner?: EyebrowBannerProps
  title: string
  loggedIn: boolean
  isOrgAdmin: boolean
  ctas: {
    trial: Cta
    sales: Cta
    signUp: SignUpField
    downloadCta: Cta
    downloadMenu: DownloadMenu
  }
  monaHead: MonaHeadProps
}

export const defaultProps: Partial<IntroProps> = {}

export const Intro = (props: IntroProps) => {
  const initializedProps: IntroProps = {...defaultProps, ...props}
  const {eyebrowBanner, title, loggedIn, isOrgAdmin, ctas, monaHead} = initializedProps
  const animatedTitle = useMemo(() => parseIntroTitle(title), [title])

  const downloadActions = useMemo(
    () => ctas.downloadMenu.actions.map(({url, ...action}) => ({...action, href: url})),
    [ctas.downloadMenu.actions],
  )

  const prefersReducedMotion = usePrefersReducedMotion()
  const {contentRef, eyebrowBannerRef, monaRef, monaHeadRef, headingRef, prepare, show, isReady, hasAnimatedIn} =
    useMotion({
      prefersReducedMotion,
      title: animatedTitle,
    })
  const {isIntersecting: isMonaVisible} = useIntersectionObserver(monaRef, {threshold: 0})
  const hasMonaBeenVisibleRef = useRef<boolean>(false)

  useEffect(() => {
    if (!isMonaVisible) return

    // If Mona starts below the fold, the intro still calls enterMona().
    // That call is saved until Mona becomes visible and her canvas is ready.
    // Don't call it again when she first appears: that would restart the animation.
    // Replay only when she leaves the screen and comes back.
    if (hasMonaBeenVisibleRef.current && hasAnimatedIn) monaHeadRef.current?.enterMona()
    hasMonaBeenVisibleRef.current = true
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMonaVisible])

  useEffect(() => {
    const cleanup = prepare()
    return cleanup
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (isReady && !prefersReducedMotion) show(0.6)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady])

  const loggedOutCtaDOM = (
    <>
      <SignUpForm
        className={Styles['Intro__ctas_signUpForm']}
        signUp={ctas.signUp}
        formLabel={ctas.signUp.cta.label}
        location="intro"
      />
      <Button
        as="a"
        href={ctas.downloadCta.url}
        className={clsx(Styles.Intro__ctas_button, Styles['Intro__ctas_button--signUpSecondary'])}
        variant="secondary"
        {...getAnalyticsEvent({
          action: ctas.downloadCta.label,
          tag: 'a',
          context: 'CTAs',
          location: 'intro',
        })}
      >
        {ctas.downloadCta.label}
      </Button>
    </>
  )

  const loggedInAdminCtaDOM = (
    <>
      <Button
        as="a"
        href={ctas.trial.url}
        className={Styles['Intro__ctas_button']}
        variant="primary"
        {...getAnalyticsEvent({
          action: ctas.trial.label,
          tag: 'a',
          context: 'CTAs',
          location: 'intro',
        })}
      >
        {ctas.trial.label}
      </Button>
      <Button
        as="a"
        href={ctas.sales.url}
        className={Styles['Intro__ctas_button']}
        variant="secondary"
        {...getAnalyticsEvent({
          action: ctas.sales.label,
          tag: 'a',
          context: 'CTAs',
          location: 'intro',
        })}
      >
        {ctas.sales.label}
      </Button>
    </>
  )

  const loggedInNonAdminCtaDOM = (
    <>
      <div className={Styles['Intro__ctas_downloadMenu']}>
        <OSAwareDownloadActionMenu
          actions={downloadActions}
          menuLabel={ctas.downloadMenu.menuLabel}
          analytics={{context: 'installer', location: 'intro', tag: 'a'}}
          variant="primary"
        />
      </div>
      <Button
        as="a"
        href={ctas.trial.url}
        variant="secondary"
        className={Styles['Intro__ctas_button']}
        {...getAnalyticsEvent({
          action: ctas.trial.label,
          tag: 'a',
          context: 'CTAs',
          location: 'intro',
        })}
      >
        {ctas.trial.label}
      </Button>
    </>
  )

  return (
    <div className={Styles['Intro']}>
      <div className={Styles['Intro__grid_content']}>
        <Grid columnGap="none" rowGap="none" enableGutters={false}>
          <Grid.Column span={12} className={Styles['Intro__grid_column']}>
            <div
              ref={contentRef}
              className={Styles['Intro__content']}
              style={prefersReducedMotion || hasAnimatedIn ? {} : {opacity: 0}}
            >
              <div className={Styles['Intro__header']}>
                {eyebrowBanner && (
                  <div ref={eyebrowBannerRef} className={Styles['Intro__eyebrowBanner']}>
                    <EyebrowBanner {...eyebrowBanner} />
                  </div>
                )}

                <Heading
                  ref={headingRef}
                  as="h1"
                  size="1"
                  textWrap="balance"
                  className={Styles['Intro__header_title']}
                  {...hpcAttrs()}
                >
                  {animatedTitle.lines.map((line, index) => (
                    <Fragment key={line.group}>
                      {index > 0 && <br />}
                      {line.text}
                    </Fragment>
                  ))}
                </Heading>
              </div>

              <div className={clsx(Styles.Intro__ctas, !loggedIn && Styles['Intro__ctas--signUp'])}>
                {!loggedIn ? loggedOutCtaDOM : isOrgAdmin ? loggedInAdminCtaDOM : loggedInNonAdminCtaDOM}
              </div>
            </div>

            <div
              ref={monaRef}
              className={Styles['Intro__mona']}
              style={prefersReducedMotion || hasAnimatedIn ? {} : {opacity: 0}}
            >
              <MonaHead {...monaHead} ref={monaHeadRef} isAutoplay={false} />
            </div>
          </Grid.Column>
        </Grid>
      </div>
    </div>
  )
}
